import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import debounce from "lodash/debounce";
import {
  fetchAdminRouteV2ControllerGetRoutesPaged,
  Tokens,
  refreshAuthResponse,
  useAuthStore,
} from "service";
import { io, Socket } from "socket.io-client";
import { shallow } from "zustand/shallow";
import {
  AdminLocationNoteChanged,
  LocationSyncFromCsvResponse,
  LocationSyncFromCsvProgressResponse,
  RecalcEstResponse,
  SocketEvents,
} from "./socket.types";
import { queryClient } from "../query-client";
import { queryKeyFn, useEstabrookContext } from "service/api/estabrookContext";
import {
  createRegionsInvalidationPredicate,
  createRoutesInvalidationPredicate,
  checkQueryKey,
} from "service/api/estabrookQueryKeys";
import useRoutePlannerStore from "../state/route-planner-store";
import {
  AdminGetRouteStateDiffResDto,
  DriverStatusDto,
} from "service/api/estabrookSchemas";
import { MapMarker } from "../components/map/map.types";
import config from "../config";
import useRegionManagementStore from "../state/region-management-store";
import { toast } from "react-toastify";
import useCsvUploadStore from "../state/csv-upload-store";
import {
  isFilteredRoutesList,
  isUnfilteredRoutesList,
  replaceRouteInList,
  refreshRoutesInCachedLists,
  RouteUpdatedPayload,
} from "./route-updated.utils";

interface UseSocketProps {
  currentPage:
    | "route-planner"
    | "region-management"
    | "customer-management"
    | "notifications"
    | "snow-reports";
  routePlannerDate?: string;
  setCsvImportStatus?: (status: "success" | "updating" | undefined) => void;
  setCsvReviewData?: (data: LocationSyncFromCsvResponse) => void;
  onCsvImportCompleted?: () => void;
}

const parseCoordinate = (value?: string | number | null) => {
  if (value === undefined || value === null || value === "") return undefined;

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

// Every driver tap reaches each open admin tab as a status event plus an EST
// event, and each one used to reload the whole planner for the date. With
// several drivers live those reloads kept the single-vCPU API busy, so a burst
// of taps is folded into one reload. The max wait keeps a steady stream of taps
// from holding the planner back for longer than that.
const LIVE_REFRESH_DEBOUNCE_MS = 1500;
const LIVE_REFRESH_MAX_WAIT_MS = 3000;
// After an EST event the server sends each recalculated route on its own
// (ADMIN_ROUTE_UPDATED). Routes that haven't arrived by then fall back to
// reloading every route, as before, e.g. when the API doesn't send them.
const ROUTE_UPDATE_WAIT_MS = 3000;
// A driver's status event comes straight from the app. The server sends the
// recalculated route after every tap and the EST event above already fetches a
// late one, so this only matters when the tap's processing failed.
const STATUS_ROUTE_UPDATE_WAIT_MS = 15000;
// The server checks live vs committed once per change and pushes the answer
// (ADMIN_ROUTE_CHANGES_CHANGED); a tap doesn't change it. While live events keep
// coming, each tab still checks itself at most this often, in case a change
// didn't push. Every tab's check is costly for the API.
const STATE_DIFF_SAFETY_CHECK_MS = 30000;

export const useSocketEvents = ({
  currentPage,
  routePlannerDate,
  setCsvImportStatus,
  setCsvReviewData,
  onCsvImportCompleted,
}: UseSocketProps) => {
  const {
    setHasLiveStateChanges,
    selectedAddresses,
    updateSelectedAddresses,
    updateDriverMarkerLiveLocation,
  } = useRoutePlannerStore(
    (state) => ({
      setHasLiveStateChanges: state.setHasLiveStateChanges,
      selectedAddresses: state.selectedAddresses,
      updateSelectedAddresses: state.updateSelectedAddresses,
      updateDriverMarkerLiveLocation: state.updateDriverMarkerLiveLocation,
    }),
    shallow,
  );
  const setRecalcEstStatus = useRegionManagementStore(
    (state) => state.setRecalcEstStatus,
  );
  const isImpersonating = useAuthStore((state) => state.isImpersonating);
  // Direct fetches need these for the auth header; socket handlers outlive
  // renders, so they read the latest through a ref.
  const { fetcherOptions } = useEstabrookContext();
  const fetcherOptionsRef = useRef(fetcherOptions);
  fetcherOptionsRef.current = fetcherOptions;
  const selectedAddressesRef = useRef<MapMarker[]>([]);
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const MAX_RETRIES = 5;
  const retryCountRef = useRef(0);
  // Stable reference so cleanupEvents can .off() the exact listener
  // registerEvents .on()'d - two separately-created inline arrows aren't
  // === to each other, so .off() would otherwise silently no-op and leak
  // a CONNECT_ERROR listener on every reconnect.
  const connectErrorHandlerRef = useRef<((err: Error) => void) | null>(null);
  const routeStateDiffDelayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const routeInvalidationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Socket handlers are registered once per connection, so the live refresh
  // reads the date from a ref instead of the closure it was created in.
  const routePlannerDateRef = useRef(routePlannerDate);
  routePlannerDateRef.current = routePlannerDate;
  const liveRefreshAllRoutesRef = useRef(false);
  // When each route last arrived through ADMIN_ROUTE_UPDATED and was
  // swapped into a routes list.
  const routeUpdatedAtRef = useRef(
    new Map<string, { at: number; date: string }>(),
  );
  const routeUpdateWaitTimeoutsRef = useRef(new Set<NodeJS.Timeout>());
  // One entry per driver status event still waiting for its recalculated route.
  // The app's status message doesn't reliably name the route, so they're
  // counted rather than matched; any left over reload the planner as before.
  const awaitingRouteSinceRef = useRef<number[]>([]);
  // Status events that do name their route wait for that route instead.
  const awaitingRouteIdsRef = useRef(new Set<string>());

  const handleConnect = () => {
    setIsConnected(true);
    retryCountRef.current = 0;
  };

  const handleDisconnect = () => setIsConnected(false);

  // Debounced route invalidation to prevent concurrent invalidation calls
  const debouncedInvalidateRoutes = useCallback(() => {
    if (routeInvalidationTimeoutRef.current) {
      clearTimeout(routeInvalidationTimeoutRef.current);
    }

    routeInvalidationTimeoutRef.current = setTimeout(() => {
      queryClient.invalidateQueries(createRoutesInvalidationPredicate());
    }, 100); // Small debounce delay to prevent concurrent calls
  }, []);

  const liveRefresh = useMemo(
    () =>
      debounce(
        () => {
          const date = routePlannerDateRef.current;
          const refreshAllRoutes = liveRefreshAllRoutesRef.current;
          liveRefreshAllRoutesRef.current = false;

          if (refreshAllRoutes || currentPage !== "route-planner" || !date) {
            queryClient.invalidateQueries(createRoutesInvalidationPredicate());
            return;
          }

          invalidateRoutePlannerExcludingStateDiff(date);
        },
        LIVE_REFRESH_DEBOUNCE_MS,
        { maxWait: LIVE_REFRESH_MAX_WAIT_MS },
      ),
    [currentPage],
  );

  const refreshRoutesInLists = (
    date: string,
    routeIds: string[],
    listFilter?: (queryKey: readonly unknown[]) => boolean,
  ) =>
    refreshRoutesInCachedLists({
      queryClient,
      fetchRoutes: async (queryParams) =>
        (
          await fetchAdminRouteV2ControllerGetRoutesPaged({
            ...fetcherOptionsRef.current,
            queryParams,
          })
        ).data ?? [],
      date,
      routeIds,
      listFilter,
      arrivedAt: (routeId) => routeUpdatedAtRef.current.get(routeId)?.at,
    });

  const handleRouteLocationStatusUpdate = (payload?: unknown) => {
    const routeId = (payload as { routeId?: unknown } | undefined)?.routeId;
    const viewedDate = routePlannerDateRef.current;
    if (
      typeof routeId === "string" &&
      routeId &&
      currentPage === "route-planner" &&
      viewedDate
    ) {
      const since = Date.now();
      awaitingRouteIdsRef.current.add(routeId);
      const timeout = setTimeout(() => {
        routeUpdateWaitTimeoutsRef.current.delete(timeout);
        const update = routeUpdatedAtRef.current.get(routeId);
        if (!update || update.at < since) {
          awaitingRouteIdsRef.current.delete(routeId);
          refreshRoutesInLists(viewedDate, [routeId]);
        }
      }, STATUS_ROUTE_UPDATE_WAIT_MS);
      routeUpdateWaitTimeoutsRef.current.add(timeout);
      scheduleStateDiffSafetyCheck(viewedDate);
      return;
    }

    awaitingRouteSinceRef.current.push(Date.now());
    const timeout = setTimeout(() => {
      routeUpdateWaitTimeoutsRef.current.delete(timeout);
      const overdueSince = Date.now() - STATUS_ROUTE_UPDATE_WAIT_MS;
      if (awaitingRouteSinceRef.current.some((at) => at <= overdueSince)) {
        awaitingRouteSinceRef.current = [];
        liveRefresh();
      }
    }, STATUS_ROUTE_UPDATE_WAIT_MS);
    routeUpdateWaitTimeoutsRef.current.add(timeout);
    // If we're on the route planner page, the state diff waits for the backend to settle
    if (currentPage === "route-planner" && routePlannerDateRef.current) {
      scheduleStateDiffSafetyCheck(routePlannerDateRef.current);
    }
  };

  const handleRegionRouteSyncCompleted = () => {
    debouncedInvalidateRoutes();
  };

  const invalidateRoutePlannerExcludingStateDiff = (date: string) => {
    // Invalidate route queries for the specific date only, except the route state diff query
    queryClient.invalidateQueries({
      predicate: (query: { queryKey: unknown }) => {
        const queryKey = query.queryKey as string[];

        // Check if it's a routes-related query using the utility function
        const isRoutesQuery = checkQueryKey(queryKey, "api/v2/admin/routes");
        if (!isRoutesQuery) return false;

        // Exclude the route state diff query specifically
        // Path: /api/v2/admin/routes/date/{date}/state/check
        // Query key: ["api", "v2", "admin", "routes", "date", "{actual-date}", "state", "check"]
        const isStateDiffQuery = checkQueryKey(
          queryKey,
          "api/v2/admin/routes/date/{date}/state/check",
        );

        // Only invalidate queries for the current date being viewed
        // Check if the query key contains the date we're currently viewing
        const queryKeyContainsDate = queryKey.some(
          (part) => typeof part === "string" && part === date,
        );

        // Check if this is a query with queryParams containing the date
        const queryKeyString = JSON.stringify(queryKey);
        const containsDateInParams = queryKeyString.includes(
          `"date":"${date}"`,
        );

        const isForCurrentDate = queryKeyContainsDate || containsDateInParams;

        // The date's notification settings only change through an admin edit,
        // never through a driver's tap.
        const isNotificationSettingsQuery = checkQueryKey(
          queryKey,
          "api/v2/admin/routes/date/{date}/notifications",
        );

        return (
          !isStateDiffQuery && !isNotificationSettingsQuery && isForCurrentDate
        ); // Invalidate only current date queries EXCEPT state diff and notification settings
      },
    });
  };

  const scheduleStateDiffSafetyCheck = (date: string) => {
    if (routeStateDiffDelayTimeoutRef.current) return;

    routeStateDiffDelayTimeoutRef.current = setTimeout(() => {
      routeStateDiffDelayTimeoutRef.current = null;
      queryClient.invalidateQueries({
        queryKey: queryKeyFn({
          path: "/api/v2/admin/routes/date/{date}/state/check",
          operationId: "adminRouteV2ControllerGetRouteStateDiff",
          variables: {
            pathParams: { date },
          },
        }),
      });
    }, STATE_DIFF_SAFETY_CHECK_MS);
  };

  const handleDriverConnectivity = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeyFn({
        path: "/api/v2/admin/driver-status",
        operationId: "adminDriverStatusControllerFindAll",
        variables: {},
      }),
    });
  };

  const handleDriverLocationUpdate = (data: DriverStatusDto) => {
    const latitude = parseCoordinate(data.latitude);
    const longitude = parseCoordinate(data.longitude);

    updateDriverMarkerLiveLocation(
      data.driverId,
      latitude === undefined || longitude === undefined
        ? undefined
        : {
            latitude,
            longitude,
          },
    );
  };

  const handleLiveStateChanges = (
    data: Pick<AdminGetRouteStateDiffResDto, "hasChanges"> & {
      date?: string;
    },
  ) => {
    // The server pushes this for the date it checked; another date's answer
    // says nothing about the planner being viewed.
    const pushedDate =
      typeof data.date === "string" ? data.date.slice(0, 10) : undefined;
    const viewedDate = routePlannerDateRef.current;
    if (pushedDate && viewedDate && pushedDate !== viewedDate) return;

    // A fresh answer makes the pending safety check unnecessary.
    if (routeStateDiffDelayTimeoutRef.current) {
      clearTimeout(routeStateDiffDelayTimeoutRef.current);
      routeStateDiffDelayTimeoutRef.current = null;
    }

    const {
      initialLiveEstSettlementDate,
      driverStatusSettlementDate,
      setDriverStatusSettlementDate,
    } = useRoutePlannerStore.getState();

    if (initialLiveEstSettlementDate === routePlannerDate && data.hasChanges) {
      setHasLiveStateChanges(false);
      return;
    }

    // While an admin-triggered status update is settling in the background,
    // ignore transient "hasChanges" reports so the Update Routes button
    // doesn't flash. A "no changes" report means the backend has settled.
    // Note: settlement only ever exists for the date currently being viewed,
    // so a presence check is enough (routePlannerDate can be a stale closure).
    if (driverStatusSettlementDate) {
      if (!data.hasChanges) {
        setDriverStatusSettlementDate(undefined);
      }
      setHasLiveStateChanges(false);
      return;
    }

    setHasLiveStateChanges(data.hasChanges);
  };

  const updateNotesCounter = (payload: AdminLocationNoteChanged) => {
    // Invalidate location notes queries to refresh the notes list
    queryClient.invalidateQueries({
      queryKey: queryKeyFn({
        path: "/api/v2/admin/locations/{locationId}/location-notes",
        operationId: "adminLocationNotesControllerFindAll",
        variables: {
          pathParams: { locationId: payload.locationId },
        },
      }),
    });

    if (
      selectedAddressesRef.current.length > 0 &&
      selectedAddressesRef.current.find(
        (item) => item.id === payload.locationId,
      )
    ) {
      const updatedSelectedAddresses = selectedAddressesRef.current.map(
        (selected) => {
          if (selected.id === payload.locationId) {
            return {
              ...selected,
              locationNoteCount: payload.locationNoteCount,
            };
          }

          return selected;
        },
      );

      updateSelectedAddresses(updatedSelectedAddresses);
    } else {
      handleRouteLocationStatusUpdate();
    }
  };

  const handleRecalcRegionEst = (response: RecalcEstResponse) => {
    if (response.status === "started") {
      setRecalcEstStatus("started");
    } else if (response.status === "success") {
      setRecalcEstStatus("success");

      queryClient.invalidateQueries(createRegionsInvalidationPredicate());

      toast.success("Regions EST calculated successfully.");
    } else {
      setRecalcEstStatus("idle");
    }
  };

  const handleRouteUpdated = (payload: RouteUpdatedPayload) => {
    if (!payload?.route?.id || !payload.date) return;

    let replaced = false;
    queryClient.setQueriesData(
      {
        predicate: (query) =>
          isUnfilteredRoutesList(query.queryKey, payload.date),
      },
      (cached: unknown) => {
        const next = replaceRouteInList(cached, payload.route);
        if (next === null) return cached;
        replaced = true;
        return next;
      },
    );
    if (replaced) {
      if (!awaitingRouteIdsRef.current.delete(payload.route.id)) {
        awaitingRouteSinceRef.current.shift();
      }
      routeUpdatedAtRef.current.set(payload.route.id, {
        at: Date.now(),
        date: payload.date,
      });
    }

    // The server filters stops for these lists, so they can't take the route
    // as sent: they fetch just this route with their own filters.
    refreshRoutesInLists(payload.date, [payload.route.id], (queryKey) =>
      isFilteredRoutesList(queryKey, payload.date),
    );
  };

  const handleRouteEstTimeCalculated = (payload?: { routeIds?: string[] }) => {
    const routeIds = payload?.routeIds ?? [];
    const receivedAt = Date.now();

    if (routeIds.length === 0) {
      liveRefreshAllRoutesRef.current = true;
      liveRefresh();
    } else {
      const timeout = setTimeout(() => {
        routeUpdateWaitTimeoutsRef.current.delete(timeout);
        const arrived = routeIds.map((routeId) =>
          routeUpdatedAtRef.current.get(routeId),
        );
        const missingRouteIds = routeIds.filter(
          (_, index) => !arrived[index] || arrived[index]!.at < receivedAt,
        );
        if (missingRouteIds.length > 0) {
          const date = routePlannerDateRef.current;
          if (currentPage === "route-planner" && date) {
            refreshRoutesInLists(date, missingRouteIds);
            scheduleStateDiffSafetyCheck(date);
          } else {
            liveRefreshAllRoutesRef.current = true;
            liveRefresh();
          }
          return;
        }

        // The swapped-in routes carry everything a recalculation changes. The
        // live and build dates, monthly states and notification settings only
        // change on go-live, dispatch, ending the day or admin edits, which
        // refresh them through their own events. Only the live-vs-committed
        // check can change, so it gets the same once-per-burst refresh as
        // after driver status events.
        const viewedDate = routePlannerDateRef.current;
        if (
          viewedDate &&
          arrived.some((update) => update!.date === viewedDate)
        ) {
          scheduleStateDiffSafetyCheck(viewedDate);
        }
      }, ROUTE_UPDATE_WAIT_MS);
      routeUpdateWaitTimeoutsRef.current.add(timeout);
    }

    // Reset the calculation state and clear pending EST calculations
    const {
      setIsCalculatingEstTime,
      clearPendingEstCalculations,
      setInitialLiveEstSettlementDate,
    } = useRoutePlannerStore.getState();
    setIsCalculatingEstTime(false);
    setInitialLiveEstSettlementDate(undefined);
    // Clear all pending EST calculations - the backend has now recalculated everything
    clearPendingEstCalculations();
  };

  const handleRouteLocationOrderUpdated = () => {
    debouncedInvalidateRoutes();
  };

  const handleCsvSyncFromCsv = (response: LocationSyncFromCsvResponse) => {
    // Check if upload has been completed or cancelled - if so, ignore this event
    const currentState = useCsvUploadStore.getState();
    if (
      (currentState.status === "idle" &&
        !currentState.reviewData &&
        !currentState.isAcceptingFromMainPage) ||
      (currentState.wasCancelled && !currentState.wasUpdate)
    ) {
      return;
    }

    if (setCsvImportStatus && setCsvReviewData && response) {
      // Always set as ready for review, regardless of validation errors
      setCsvImportStatus("success");
      setCsvReviewData(response);

      // Store the data in the upload store for ready-for-review state
      useCsvUploadStore
        .getState()
        .setReadyForReview(
          response,
          response.validationError > 0
            ? `Upload completed with ${response.validationError} validation errors`
            : "Customer Upload Ready",
        );

      queryClient.invalidateQueries({
        queryKey: queryKeyFn({
          path: "/api/v1/admin/customers-locations",
          operationId: "adminCustomerLocationControllerGetAll",
          variables: {},
        }),
      });
    }
  };

  const handleCsvSyncFromCsvCompleted = () => {
    // Check if this was a cancellation by looking at the store state
    const currentState = useCsvUploadStore.getState();

    // Trigger success callback if:
    // 1. It wasn't cancelled, OR
    // 2. It was cancelled but was actually an update (wasUpdate flag)
    const shouldTriggerCallback =
      !currentState.wasCancelled || currentState.wasUpdate;

    if (shouldTriggerCallback) {
      // Use the new function that resets state AND calls the success callback
      const { completeUploadWithSuccess } = useCsvUploadStore.getState();
      completeUploadWithSuccess(onCsvImportCompleted);
    }
  };

  const handleCsvSyncFromCsvProgress = (
    response: LocationSyncFromCsvProgressResponse,
  ) => {
    // Check if upload was cancelled - if so, ignore progress updates from old upload
    const currentState = useCsvUploadStore.getState();
    if (currentState.wasCancelled && !currentState.wasUpdate) {
      return;
    }

    // Handle progress updates - backend sends percentage without status field
    if (typeof response.percentage === "number") {
      // Only log major progress milestones to reduce spam
      if (
        response.percentage === 0 ||
        response.percentage === 100 ||
        response.percentage % 25 === 0
      ) {
        // Log progress at key intervals for monitoring
      }
      useCsvUploadStore
        .getState()
        .setProgress(response.percentage, response.message);
    }

    // Handle explicit status updates (if provided)
    if (response.status === "completed") {
      // Don't call completeUpload() here as it will reset the wasUpdate flag
      // Let the handleCsvSyncFromCsvCompleted event handle the completion
      // This just sets the progress to 100%
      useCsvUploadStore.getState().setProgress(100, "Upload completed");
    } else if (response.status === "error") {
      useCsvUploadStore.getState().errorUpload(response.message);
    }
  };

  const handleAuthError = async (error: Error, socket: Socket) => {
    retryCountRef.current += 1;
    if (retryCountRef.current > MAX_RETRIES) {
      console.error("Max retries reached. Disconnecting socket...");
      socket.disconnect();
      return;
    }

    if (error.message && error.message.includes("Authentication error")) {
      // Attempt token refresh
      socket.disconnect();
      const refreshedTokens = await refreshAuthResponse();
      if (refreshedTokens?.accessToken) {
        await connectSocket(refreshedTokens?.accessToken);
      } else {
        console.error("Token refresh failed, disconnecting...");
        socket.disconnect();
      }
    } else {
      setTimeout(() => socket.connect(), 1000); // Retry with delay
    }
  };

  const handleServiceAlertCreated = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeyFn({
        path: "/api/v2/admin/service-alerts",
        operationId: "adminServiceAlertsControllerFindMany",
        variables: {},
      }),
    });
  };

  const handleWeatherReportCreated = () => {
    // Invalidate all weather report related queries
    queryClient.invalidateQueries({
      predicate: (query: { queryKey: unknown }) => {
        const queryKey = query.queryKey as string[];
        // Check if it's a weather-report related query
        return queryKey?.includes("weather-reports");
      },
    });
  };

  const cleanupEvents = (socket: Socket | null) => {
    if (!socket) return;

    socket.off(SocketEvents.CONNECT, handleConnect);
    socket.off(SocketEvents.DISCONNECT, handleDisconnect);
    if (connectErrorHandlerRef.current) {
      socket.off(SocketEvents.CONNECT_ERROR, connectErrorHandlerRef.current);
      connectErrorHandlerRef.current = null;
    }
    socket.off(
      SocketEvents.ADMIN_ROUTE_LOCATION_STATUS_UPDATE,
      handleRouteLocationStatusUpdate,
    );
    socket.off(
      SocketEvents.ADMIN_DRIVER_TOOK_ROUTE,
      handleRouteLocationStatusUpdate,
    );
    socket.off(
      SocketEvents.ADMIN_DRIVER_PLOW_BREAKDOWN,
      handleRouteLocationStatusUpdate,
    );
    socket.off(SocketEvents.DRIVER_CONNECT, handleDriverConnectivity);
    socket.off(SocketEvents.DRIVER_DISCONNECT, handleDriverConnectivity);
    socket.off(
      SocketEvents.ADMIN_DRIVER_STATUS_LOCATION_UPDATE,
      (data: DriverStatusDto) => handleDriverLocationUpdate(data),
    );
    socket.off(
      SocketEvents.ADMIN_ROUTE_CHANGES_CHANGED,
      (data: AdminGetRouteStateDiffResDto & { date?: string }) =>
        handleLiveStateChanges(data),
    );
    socket?.off(
      SocketEvents.ADMIN_ROUTE_DRIVER_NOTE_CHANGED,
      updateNotesCounter,
    );
    socket.off(
      SocketEvents.ADMIN_SYNC_ROUTE_WITH_REGION,
      handleRegionRouteSyncCompleted,
    );
    socket.off(
      SocketEvents.ADMIN_ROUTE_LOCATION_ORDER_UPDATED,
      handleRouteLocationOrderUpdated,
    );
    socket.off(SocketEvents.SERVICE_ALERT_CREATED, handleServiceAlertCreated);
    socket.off(SocketEvents.WEATHER_REPORT_CREATED, handleWeatherReportCreated);

    socket.off(SocketEvents.ADMIN_RECALCULATE_EST, handleRecalcRegionEst);
    socket.off(
      SocketEvents.ADMIN_ROUTE_EST_TIME_CALCULATED,
      handleRouteEstTimeCalculated,
    );
    socket.off(SocketEvents.ADMIN_ROUTE_UPDATED, handleRouteUpdated);

    // Cleanup CSV sync event for customer management page
    if (currentPage === "customer-management") {
      socket.off(SocketEvents.LOCATION_SYNC_FROM_CSV, handleCsvSyncFromCsv);
      socket.off(
        SocketEvents.LOCATION_SYNC_FROM_CSV_COMPLETED,
        handleCsvSyncFromCsvCompleted,
      );
      socket.off(
        SocketEvents.LOCATION_SYNC_FROM_CSV_PROGRESS,
        handleCsvSyncFromCsvProgress,
      );
    }

    socket.disconnect();
  };

  const registerEvents = (socket: Socket | null) => {
    if (!socket) return;

    socket.on(SocketEvents.CONNECT, handleConnect);
    socket.on(SocketEvents.DISCONNECT, handleDisconnect);

    socket.on(
      SocketEvents.ADMIN_ROUTE_LOCATION_STATUS_UPDATE,
      handleRouteLocationStatusUpdate,
    );
    socket.on(
      SocketEvents.ADMIN_DRIVER_TOOK_ROUTE,
      handleRouteLocationStatusUpdate,
    );
    socket.on(
      SocketEvents.ADMIN_DRIVER_PLOW_BREAKDOWN,
      handleRouteLocationStatusUpdate,
    );

    socket.on(SocketEvents.DRIVER_CONNECT, handleDriverConnectivity);
    socket.on(SocketEvents.DRIVER_DISCONNECT, handleDriverConnectivity);

    socket.on(
      SocketEvents.ADMIN_DRIVER_STATUS_LOCATION_UPDATE,
      (data: DriverStatusDto) => handleDriverLocationUpdate(data),
    );

    socket.on(
      SocketEvents.ADMIN_ROUTE_CHANGES_CHANGED,
      (data: AdminGetRouteStateDiffResDto & { date?: string }) =>
        handleLiveStateChanges(data),
    );

    connectErrorHandlerRef.current = (err) => handleAuthError(err, socket);
    socket.on(SocketEvents.CONNECT_ERROR, connectErrorHandlerRef.current);
    socket.on(SocketEvents.ADMIN_ROUTE_DRIVER_NOTE_CHANGED, updateNotesCounter);
    socket.on(
      SocketEvents.ADMIN_SYNC_ROUTE_WITH_REGION,
      handleRegionRouteSyncCompleted,
    );
    socket.on(
      SocketEvents.ADMIN_ROUTE_LOCATION_ORDER_UPDATED,
      handleRouteLocationOrderUpdated,
    );
    socket.on(SocketEvents.SERVICE_ALERT_CREATED, handleServiceAlertCreated);
    socket.on(SocketEvents.WEATHER_REPORT_CREATED, handleWeatherReportCreated);

    socket.on(SocketEvents.ADMIN_RECALCULATE_EST, handleRecalcRegionEst);
    socket.on(
      SocketEvents.ADMIN_ROUTE_EST_TIME_CALCULATED,
      handleRouteEstTimeCalculated,
    );
    socket.on(SocketEvents.ADMIN_ROUTE_UPDATED, handleRouteUpdated);

    // Register CSV sync event for customer management page
    if (currentPage === "customer-management") {
      socket.on(SocketEvents.LOCATION_SYNC_FROM_CSV, handleCsvSyncFromCsv);
      socket.on(
        SocketEvents.LOCATION_SYNC_FROM_CSV_COMPLETED,
        handleCsvSyncFromCsvCompleted,
      );
      socket.on(
        SocketEvents.LOCATION_SYNC_FROM_CSV_PROGRESS,
        handleCsvSyncFromCsvProgress,
      );
    }
  };

  const connectSocket = async (token: string | undefined) => {
    if (!token) return;

    // WebSocket-only, with the token in the handshake: long-polling needs
    // every request of a session to reach the same API task, which breaks
    // once the API runs more than one task. Browsers can't send headers on a
    // WebSocket, so the token can't go in extraHeaders.
    socketRef.current = io(config.socketUrl, {
      transports: ["websocket"],
      auth: {
        token: `Bearer ${token}`,
      },
      reconnection: false,
    });

    registerEvents(socketRef.current);
  };

  useEffect(() => {
    const tokens = Tokens.retrieveFromLocalStorage();
    const accessToken = tokens?.accessToken;
    if (
      currentPage === "route-planner" ||
      currentPage === "region-management" ||
      currentPage === "customer-management" ||
      currentPage === "notifications" ||
      currentPage === "snow-reports"
    ) {
      connectSocket(accessToken);
    } else if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    return () => {
      // Clear any pending timeouts
      if (routeStateDiffDelayTimeoutRef.current) {
        clearTimeout(routeStateDiffDelayTimeoutRef.current);
        routeStateDiffDelayTimeoutRef.current = null;
      }
      awaitingRouteIdsRef.current.clear();
      if (routeInvalidationTimeoutRef.current) {
        clearTimeout(routeInvalidationTimeoutRef.current);
      }
      liveRefresh.cancel();
      routeUpdateWaitTimeoutsRef.current.forEach(clearTimeout);
      routeUpdateWaitTimeoutsRef.current.clear();
      cleanupEvents(socketRef.current);
    };
    // isImpersonating is included so the socket reconnects with the freshly
    // swapped token when a superadmin starts/stops impersonating an
    // organization without changing currentPage - otherwise this effect
    // would only ever read the token once per page and keep using a stale
    // session (see memory-bank/frontend-onboarding.md, "Impersonation
    // mechanics" - "force the socket to reconnect").
  }, [currentPage, isImpersonating]);

  useEffect(() => {
    selectedAddressesRef.current = selectedAddresses;
  }, [selectedAddresses]);

  return { socket: socketRef.current, isConnected };
};
